import type {
  Diagnostics,
  AvailableReport,
  CallerLink,
  Department,
  DepartmentAllocation,
  EmployeeAllocations,
  EmployeeDepartmentSyncResult,
  Employee,
  EmployeeSupervisorMapping,
  EmployeeSyncSummary,
  Kiosk,
  KioskEnrollment,
  ListSessionsScope,
  NsConnectionConfig,
  EmployeeEarningAssignment,
  PayException,
  SupervisorPeerCandidate,
  SupervisorPeerGroup,
  SupervisorPeerGroupInput,
  PayExceptionWire,
  NsPayrollItem,
  CompanyHoliday,
  CompanyHolidayInput,
  HolidayPay,
  HolidayPeriodPay,
  ManualTimeEntryInput,
  AmountEntry,
  AmountEntryInput,
  AmountEntryOptions,
  AmountReportId,
  DriverLogInput,
  DriverLogWeek,
  PtoAccrualSnapshot,
  PtoStanding,
  PtoEntry,
  PtoEntryInput,
  PtoOptions,
  AmountRecurring,
  AmountRecurringInput,
  AmountPostPlan,
  AmountPostResult,
  CommissionPostPlan,
  CommissionPostResult,
  CommissionReport,
  TimeEntryOptions,
  RoundingRule,
  SessionPhotosResponse,
  SetUserEmployeeLinkRequest,
  TimeBillPostResult,
  TimeSession,
  BonusAlias,
  ProductionLinesView,
  BonusImportResult,
  LivenessResult,
  StoredReportSchedule,
  ReportScheduleInput,
  ReportScheduleRun,
  ReportRunRequest,
  ReportPreview,
  ReportWorkbook,
} from '@worklogix/shared';
import { ApiError, createApiClient, useAuth } from '@worklogix/web-common';
import type { ApiClient } from '@worklogix/web-common';
import { useMemo } from 'react';

// Re-export so time/admin components can branch on transport failures (e.g. a
// 409 on a PIN collision or a 502 from the NetSuite poster stub) without
// reaching into the web-common client module directly.
export { ApiError };

/**
 * Server-side filters for `GET /time/sessions`. All optional — an empty filter
 * lists every session the caller's tenant/role can see. Dates are ISO-8601
 * (date or datetime); the server bounds `clock_in_at` to the [from, to] window.
 */
export interface SessionFilters {
  /** Inclusive lower bound on the session day (ISO date, e.g. `2026-08-01`). */
  from?: string;
  /** Inclusive upper bound on the session day (ISO date). */
  to?: string;
  /** Restrict to one department. */
  departmentId?: string;
  /** Restrict to one employee. */
  employeeId?: string;
  /**
   * Admin-only supervisor picker: the supervisor's OWN employee id. Scopes the
   * list to that supervisor's DIRECT reports (server-enforced; ignored for a
   * non-admin Manager, who is always constrained to their own reports).
   */
  supervisorId?: string;
  /**
   * Report-set scope (plan-v9 Phase 4): `all` = every session in the tenant
   * (Admin only), `mine` = only the caller's reports. Omitted for a non-admin
   * Manager — the server ALWAYS constrains them to their report set regardless,
   * so the client never asserts a scope it can't widen.
   */
  scope?: ListSessionsScope;
  /**
   * Server-side status filter.
   *
   * `long_shift` is the one this app sends on its own: it selects every session past
   * the 10-hour mark REGARDLESS of the date window, which is the whole point of it.
   * The case that matters — clocked in last night and never clocked out — has a
   * clock-in on YESTERDAY, so a supervisor looking at today would never be shown it.
   */
  status?: SessionStatusFilter;
  /**
   * Filter by the LAST post attempt's outcome. A SEPARATE dimension from `status`, so
   * both narrow at once — "unapproved AND failed to post" is the real question.
   */
  postResult?: PostResultFilter;
}

/**
 * The server's `postResult` values (`api/src/time/dto/list-sessions.query.ts`). Pinned
 * here for the same reason as the status values below: the shared contract does not
 * define the query DTO.
 */
export type PostResultFilter = 'posted' | 'skipped' | 'failed' | 'not_attempted';

/**
 * The server's `status` filter values (`api/src/time/dto/list-sessions.query.ts`). Pinned
 * here rather than imported because the shared contract does not define the query DTO.
 */
/**
 * What one approval did, per day (`POST /reports/pto/:id/approve`). Pinned here rather
 * than imported because the shared contract does not define it — the same treatment as
 * {@link UserEmployeeLink}.
 *
 * `failed` non-empty means a PARTIAL post: those days did not reach NetSuite and the
 * booking is NOT approved. Retrying posts only the missing days.
 */
export interface PtoPostResult {
  entryId: string;
  /** date -> NetSuite time entry id, for the days that posted. */
  posted: Record<string, string>;
  failed: { date: string; reason: string }[];
}

export type SessionStatusFilter =
  | 'open'
  | 'unapproved'
  | 'approved'
  | 'posted'
  | 'needs_review'
  | 'long_shift';

/**
 * One selectable supervisor for the Admin-only picker (`GET /time/supervisors`).
 * `employeeId` is the supervisor's OWN employee id — the value sent back as the
 * `supervisorId` list filter. Matches the API's `SupervisorOption` projection;
 * the shared contract does not define this row, so it is pinned here (like
 * {@link UserEmployeeLink}).
 */
export interface TimeSupervisor {
  employeeId: string;
  nsEmployeeId: string;
  displayName: string;
}

/**
 * Per-session outcome of a bulk approve (`POST /time/sessions/approve-bulk`).
 * `ok` = now approved (idempotent if already); otherwise `reason` is a short,
 * non-PII message. Matches the API's `BulkApproveOutcome`.
 */
export interface BulkApproveOutcome {
  id: string;
  ok: boolean;
  reason?: string;
}

/**
 * One Entra login row for the admin "employee link" control (plan-v9 Phase 4
 * `GET /netsuite/user-links`).
 *
 * Matches Dev-B's actual `UserEmployeeLink` (directory-sync.service.ts) — the
 * shared contract does not define this list row, so the shape is pinned to the
 * backend projection of `app.app_user`. `userId` is the `:id` in
 * `PATCH /users/:id/employee-link`.
 */
export interface UserEmployeeLink {
  /** `app.app_user` id — the `:id` in `PATCH /users/:id/employee-link`. */
  userId: string;
  /** The Entra identity key (oid/sub) the login authenticates with. */
  externalId: string;
  /** The Entra sign-in email / UPN; null when unknown. */
  email: string | null;
  /** Display name from the Entra token, when known. */
  displayName: string | null;
  /** Currently-linked NS employee internal id; null when unlinked. */
  nsEmployeeId: string | null;
  /** How the link was set; null when unlinked. */
  linkSource: 'email_auto' | 'admin_manual' | null;
}

/**
 * Body for `PATCH /time/sessions/:id` — supervisor inline correction. Both
 * fields are optional; omit one to leave it unchanged. Mirrors the shipped
 * `TimeSession` shape where `clockOutAt` is an optional ISO string (not nullable).
 */
/**
 * Result of the consolidated post (#10) — bonuses, reimbursements and commission in one
 * press. Declared here rather than imported: the API owns the shape, and the web only
 * needs to render it.
 *
 * Each leg is INDEPENDENT. `problem` set with `posted: 0` is the normal "nothing to
 * post" case, not a failure — most weeks carry no reimbursements.
 */
export interface ConsolidatedPostResult {
  payPeriodEnding: string;
  legs: Array<{
    reportId: string;
    posted: number;
    failed: number;
    failures: Array<{ employeeName: string; reason: string }>;
    problem?: string;
  }>;
  totalPosted: number;
  totalFailed: number;
}

/** Result of the ALLOW-earning sweep (#9). Creates entries for review; posts nothing. */
export interface AllowanceSweepResult {
  payPeriodEnding: string;
  created: number;
  existing: number;
  skipped: number;
  rows: Array<{
    employeeName: string;
    itemName: string;
    nsPayrollItemId: string;
    amount: number;
    outcome: 'created' | 'existing' | 'skipped';
    reason?: string;
  }>;
  /** Oldest mirror sync behind these rates — a stale mirror explains a surprising result. */
  oldestSyncedAt: string | null;
}

export interface PatchSessionInput {
  /** New clock-in instant (ISO-8601). Omit to leave unchanged. */
  clockInAt?: string;
  /** New clock-out instant (ISO-8601). Omit to leave unchanged. */
  clockOutAt?: string;
  /**
   * Reallocate the session to a different department. ADMIN ONLY — the API refuses a
   * change from anyone else, because department selects the earning type the hours
   * post under and so moves money to a different line on the paycheck.
   *
   * The DTO has accepted this field all along; the client simply never sent it.
   */
  departmentId?: string;
}

/** Body for `POST /admin/employees`. Never carries a PIN. */
export interface CreateEmployeeInput {
  /** Human-readable name shown in the roster and on sessions. */
  displayName: string;
  /** NetSuite employee id when the directory is wired; omitted pre-sync. */
  nsEmployeeId?: string;
}

/** Body for `POST /admin/kiosks` — bind a new kiosk device to a department. */
export interface RegisterKioskInput {
  /** Friendly kiosk/tablet name (e.g. "Blend Line 2 Tablet"). */
  name: string;
  /** Department this kiosk punches into (device is bound to this department). */
  departmentId: string;
}

/**
 * Body for `PATCH /netsuite/connection` — the editable NON-SECRET fields plus the
 * write gate. No secret name or value is ever part of this payload. Dev-B does NOT
 * surface KV secret names or the vault URI; there is no secret-refs shape.
 */
export interface UpdateNsConnectionInput {
  mode: 'local' | 'netsuite';
  postingEnabled: boolean;
  account?: string;
  restHost?: string;
  subsidiaryId?: string;
  defaultLocationId?: string;
  defaultPayrollItemId?: string;
  customForm?: string;
  timeType?: string;
  roundingRule: RoundingRule;
  /** IANA payroll timezone the business day is computed in (M4). */
  payrollTimezone?: string;
  /** Opt-in to the connection-default payroll-item fallback (M3; default OFF). */
  allowDefaultPayrollItem?: boolean;
}

/**
 * Summary of a department sync (Dev-B `POST /netsuite/sync/departments`). Name-
 * matches WorkLogix departments to NetSuite and reports the two unmatched sides.
 */
export interface DepartmentSyncResult {
  /** WLX departments matched + populated with an ns_department_id this run. */
  matched: number;
  /** WLX department NAMES left unmatched (need a manual override). */
  unmatchedWlx: string[];
  /** NetSuite department NAMES with no WLX counterpart (reported, never auto-created). */
  unmatchedNs: string[];
  /** WLX department NAMES that matched >1 NS department (ambiguous; map explicitly). */
  ambiguous: string[];
}

/** Summary of a payroll-item sync (Dev-B `POST /netsuite/sync/payroll-items`). */
export interface PayrollItemSyncResult {
  /** Rows upserted into the tenant's synced payroll-item table. */
  upserted: number;
}

/**
 * Summary of an employee-earnings mirror run (`POST /netsuite/sync/employee-earnings`).
 * The mirror is a FULL REPLACE, so these counts describe the whole tenant, not a delta.
 */
export interface EmployeeEarningSyncResult {
  /** (employee, earning) assignments written. */
  linked: number;
  /** Distinct employees that received at least one earning. */
  employees: number;
  /** Assignments dropped because their NS employee has no WorkLogix row. */
  skippedUnknownEmployee: number;
  /** Assignments that landed WITH a pay rate (an all-null result is a red flag). */
  withRate: number;
}

/** Body for `PATCH /netsuite/departments/:id/mapping` — manual NS department override. */
export interface UpdateDepartmentMappingInput {
  /** NS department internal id; `null` clears the mapping. */
  nsDepartmentId: string | null;
}

/** Body for `PATCH /admin/employees/:id` — NS employee + payroll-item mapping. */
export interface UpdateEmployeeMappingInput {
  /** NS employee internal id (HR-locked master → manual entry); `null` clears it. */
  nsEmployeeId?: string | null;
  /** NS payroll (earning-type) item id from the synced picker; `null` clears it. */
  nsPayrollItemId?: string | null;
}

/**
 * Employee roster row. Dev-B's `GET /admin/employees` returns the shared
 * {@link Employee} DTO, which now carries the per-employee earning type
 * (`nsPayrollItemId`, migration 0020). There is no separate roster shape — this
 * alias is kept so the roster components read as intent-named `EmployeeRow`s.
 * (The timebill-history `suggestedNsEmployeeId` Dev-C sketched is not surfaced by
 * the backend and has been dropped.)
 */
export type EmployeeRow = Employee;

/** Response of `GET /netsuite/rep-sales`, keyed by WLX employee id. */
export interface RepSalesResponse {
  byEmployeeId: Record<string, { sales: number; tons: number }>;
  /** Reps with no NetSuite id — their sales cannot be looked up at all. */
  unmappedEmployeeIds: string[];
  /** Invoice lines whose item had no usable weight; they contributed ZERO tons. */
  unweighedLines: number;
}

/**
 * Typed facade over the Entra-authed, tenant-scoped time + admin API
 * (`/time/*` and `/admin/*`, Manager/Admin only — the API enforces the role;
 * RLS is the tenant boundary server-side). Every method is a thin, strongly
 * typed wrapper over the shared {@link ApiClient}; shapes are the shared DTOs so
 * the API contract is the single source of truth.
 */

/** One paycheck in the payroll-run preview. */
export interface PayrollRunPaycheck {
  paycheckId: string;
  employee: string | null;
  status: string | null;
  departments: { unapplyCount: number; hoursMoved: number; addEarnings: number; note?: string } | null;
  salarySplit: {
    lineCount: number;
    grossBefore: number;
    grossAfter: number;
    conserves: boolean;
    reason: string | null;
  } | null;
  problem?: string;
}

/**
 * What the payroll-run button returns. PREVIEW ONLY — `committed` is always false
 * and the API refuses `commit` outright; see PayrollRunService for why.
 */
export interface PayrollRunPreview {
  batch: {
    batchnumber: string;
    periodStart: string | null;
    periodEnding: string | null;
    payDate: string | null;
    paycheckCount: number;
    statuses: Record<string, number>;
  };
  paychecks: PayrollRunPaycheck[];
  amounts: Array<{ reportId: string; rows: unknown[]; problem?: string }>;
  totals: {
    paychecksWithDepartmentWork: number;
    paychecksWithSalarySplit: number;
    hoursMoved: number;
    amountRowsToPost: number;
  };
  warnings: string[];
  committed: false;
}

export interface TimeAdminApi {
  // --- Hours & Timesheet (/time) ---
  /** `GET /time/sessions` — list/filter time sessions for review. */
  listSessions: (filters?: SessionFilters) => Promise<TimeSession[]>;
  /** `PATCH /time/sessions/:id` — inline-correct clock-in/out. */
  patchSession: (id: string, input: PatchSessionInput) => Promise<TimeSession>;
  /**
   * `GET /time/supervisors` (Admin) — the tenant's supervisors for the picker.
   * Selecting one scopes the list to that supervisor's direct reports.
   */
  listSupervisors: () => Promise<TimeSupervisor[]>;
  /**
   * `POST /time/sessions/approve-bulk` — "Approve selected". Each id is authorized
   * + approved server-side individually; the per-id outcome list is returned so a
   * partial success can be surfaced.
   */
  approveSessions: (sessionIds: string[]) => Promise<BulkApproveOutcome[]>;
  /**
   * `POST /time/sessions/:id/approve` — approve one session for posting.
   * Authorization (caller supervises the session's employee, or Admin override)
   * is enforced SERVER-SIDE; the UI only mirrors it (plan-v9 Phase 4).
   */
  approveSession: (id: string) => Promise<TimeSession>;
  /**
   * `POST /time/sessions/:id/ack-flag` — "this entry is fine": accept a session's
   * DURATION flags (too short, too long) without approving the hours.
   *
   * 🔥 A SEPARATE CALL FROM APPROVE, deliberately (Michael, 2026-09-30: "We need to
   * add a button that okays away a flag for time entries that seem too short or too
   * long"). A too-short entry carries `needsReview`, and the NetSuite poster skips
   * anything carrying it — so an approved six-minute entry used to sit unpostable
   * forever with nothing on screen saying why. Approving does NOT imply this and must
   * not: folding them together would make one bulk approval wave through every short
   * entry in the selection.
   */
  acknowledgeSessionFlags: (id: string) => Promise<TimeSession>;
  /**
   * `GET /time/session-options` — the employees this caller may add time for
   * (their NetSuite reports; every active hourly employee for an Admin) plus the
   * active departments. Exists because `GET /admin/employees` is Admin-only, so a
   * supervisor cannot read the roster.
   */
  getSessionOptions: () => Promise<TimeEntryOptions>;
  /**
   * `POST /time/sessions` — hand-enter a completed shift for an employee who
   * forgot to clock in. Created UNAPPROVED, so it joins the normal review queue
   * rather than going straight to payroll. Authorization is enforced SERVER-SIDE.
   */
  createSession: (input: ManualTimeEntryInput) => Promise<TimeSession>;
  /**
   * `GET /time/me/link` — the caller's resolved employee link (plan-v9 Phase 4).
   * Drives the "not linked" banner and whether the caller can approve anything.
   */
  getMyLink: () => Promise<CallerLink>;
  /**
   * `POST /time/post-to-netsuite` — post all approved+unposted sessions (grouped
   * server-side). Dev-B returns the per-entry {@link TimeBillPostResult} array
   * DIRECTLY (each `posted` / `skipped` / `failed` with its `sessionIds`); the
   * caller derives the aggregate counts + per-session badges from it.
   */
  postToNetSuite: () => Promise<TimeBillPostResult[]>;
  /**
   * `POST /payroll-run/preview` — plan the corrections for the latest calculated
   * batch. Admin only, and it WRITES NOTHING.
   */
  previewPayrollRun: () => Promise<PayrollRunPreview>;
  /**
   * `DELETE /admin/time-sessions/:id` — hard-delete a time session + its backing
   * punches (admin-only; refuses a posted session). Resolves with no body (204).
   */
  deleteTimeSession: (id: string) => Promise<void>;
  /**
   * `GET /time/sessions/:id/photos` — punch-photo metadata (open/close) for one
   * session (Phase 2). Carries NO bytes; images stream separately through the
   * RLS-checked proxy `GET /time/photos/:photoId`.
   */
  listSessionPhotos: (id: string) => Promise<SessionPhotosResponse>;

  // --- Directory (shared read for filters + kiosk binding) ---
  /** `GET /admin/departments` — active departments (filter + kiosk binding). */
  listDepartments: () => Promise<Department[]>;

  /** The "My Reports" items the signed-in user may see (server-filtered). */
  listAvailableReports: () => Promise<AvailableReport[]>;

  // --- Company holiday calendar (/reports/holiday, ADMIN ONLY) ---
  /** `GET /reports/holiday` — the whole calendar, soonest first. */
  listHolidays: () => Promise<CompanyHoliday[]>;
  /**
   * `GET /reports/holiday/:id/pay` — who is paid for one holiday and what it
   * costs (rate x 10 hours per paid day), with anyone lacking a rate flagged.
   */
  getHolidayPay: (id: string) => Promise<{ holiday: CompanyHoliday; pay: HolidayPay }>;
  /**
   * `GET /reports/holiday/period-pay` — what the payroll period being worked owes
   * in holiday pay. Omit `date` for the current period (resolved in the payroll
   * timezone by the server, not the browser).
   */
  getHolidayPeriodPay: (date?: string) => Promise<HolidayPeriodPay>;
  createHoliday: (input: CompanyHolidayInput) => Promise<CompanyHoliday>;
  updateHoliday: (id: string, input: CompanyHolidayInput) => Promise<CompanyHoliday>;
  deleteHoliday: (id: string) => Promise<void>;

  // --- PTO report (/reports/pto, Manager + Admin) ---
  /** Every PTO booking in the tenant — all holders of the report see all rows. */
  listPtoEntries: () => Promise<PtoEntry[]>;
  /** Active employees (hourly AND salary) + active departments for the row pickers. */
  getPtoOptions: () => Promise<PtoOptions>;
  /**
   * `GET /reports/pto/accrual/:nsEmployeeId` — the employee's NetSuite PTO standing.
   *
   * SEPARATE FROM THE ENTRY LIST on purpose: the figures come from the employee's most
   * recent PAYCHECK, and finding one costs a ~30s NetSuite search, so this is fetched
   * for ONE employee when a supervisor is actually looking at them — never per row.
   *
   * `totalRemainingHours: null` is a legitimate answer meaning NOT KNOWN, and that
   * includes "still loading" on the first look at someone: the API answers straight
   * away and finishes the lookup in the background, so a second look has the number.
   */
  getPtoAccrual: (nsEmployeeId: string) => Promise<PtoAccrualSnapshot>;
  /**
   * `GET /reports/pto/standing/:nsEmployeeId` — the FOUR figures for one employee:
   * allotted, already used, logged for future use, and what would remain if those are
   * taken. Cheap (two reads), unlike the accrual+paycheck approach it replaced.
   */
  getPtoStanding: (nsEmployeeId: string) => Promise<PtoStanding>;
  /**
   * `POST /reports/pto/:id/approve` — approve the booking AND post it to NetSuite as PTO
   * time entries. ADMIN only, and one call does both: an entry marked approved that never
   * reached NetSuite would tell somebody their time off is booked when it is not.
   */
  approvePtoEntry: (id: string) => Promise<PtoPostResult>;
  createPtoEntry: (input: PtoEntryInput) => Promise<PtoEntry>;
  /** Employee is NOT editable — delete and re-create to move a booking. */
  updatePtoEntry: (id: string, input: Omit<PtoEntryInput, 'employeeId'>) => Promise<PtoEntry>;
  deletePtoEntry: (id: string) => Promise<void>;

  // --- Amount reports: bonus / commission / reimbursement (Manager + Admin) ---
  // One set of methods for all three, with the report in the path — they are the
  // same record differing only in which earning kind the posting step resolves.
  /**
   * `GET /netsuite/rep-sales` — net invoiced dollars and TONS per rep for a date
   * range; the NetSuite half of the commission report.
   *
   * `unmappedEmployeeIds` and `unweighedLines` come back rather than being
   * folded into the figures: on a commission report an under-count looks exactly
   * like an answer, so the caller must be able to say "this is incomplete".
   */
  getRepSales: (
    employeeIds: readonly string[],
    from: string,
    to: string
  ) => Promise<RepSalesResponse>;
  /**
   * `GET /reports/commission` — the commission report for a payroll period,
   * read from NetSuite. Read-only by design: there is no create/update/delete.
   */
  getCommissionReport: (payPeriodEnding: string) => Promise<CommissionReport>;
  /**
   * `GET /reports/amounts/:reportId/recurring` — standing amounts that repeat
   * every pay period until stopped.
   */
  listRecurringAmounts: (reportId: AmountReportId) => Promise<AmountRecurring[]>;
  /** `POST .../recurring` — start one. It produces an entry each period. */
  createRecurringAmount: (
    reportId: AmountReportId,
    input: AmountRecurringInput
  ) => Promise<AmountRecurring>;
  /**
   * `DELETE .../recurring/:id` — stop one. Future periods stop; periods already
   * created are left alone, because stopping is not a clawback.
   */
  stopRecurringAmount: (reportId: AmountReportId, id: string) => Promise<void>;
  /**
   * `GET /reports/amounts/:reportId/post-preview` — what posting this period
   * WOULD do, per entry. Changes nothing; this is what a human approves before
   * money moves, and it comes from the same builder the write uses.
   */
  getAmountPostPreview: (
    reportId: AmountReportId,
    payPeriodEnding: string
  ) => Promise<AmountPostPlan>;
  /** `POST /reports/amounts/:reportId/post` — WRITES TO PAYROLL. Admin only. */
  postAmounts: (reportId: AmountReportId, payPeriodEnding: string) => Promise<AmountPostResult>;
  /**
   * `POST /reports/amounts/post-all` (Admin) — bonuses, reimbursements AND commission
   * in one press (#10). WRITES TO PAYROLL.
   */
  postAllAmounts: (payPeriodEnding: string) => Promise<ConsolidatedPostResult>;
  /**
   * `POST /reports/amounts/reimbursement/sweep-allowances` (Admin) — pull every held
   * ALLOW earning into the period's reimbursement table (#9). Creates entries for
   * review; posts nothing. Idempotent.
   */
  sweepAllowances: (payPeriodEnding: string) => Promise<AllowanceSweepResult>;
  /**
   * `GET /reports/schedules` (Admin) — the automatic report builder's schedules (#4).
   *
   * Admin only on the server too. A schedule is a STANDING grant of access to payroll
   * data that leaves the app by email, which is more power than reading the same
   * report on screen.
   */
  listReportSchedules: () => Promise<StoredReportSchedule[]>;
  /** `POST /reports/schedules` (Admin) — create one. Validated server-side. */
  createReportSchedule: (input: ReportScheduleInput) => Promise<StoredReportSchedule>;
  /** `PUT /reports/schedules/:id` (Admin) — replace one. */
  updateReportSchedule: (
    id: string,
    input: ReportScheduleInput
  ) => Promise<StoredReportSchedule>;
  /** `DELETE /reports/schedules/:id` (Admin). */
  deleteReportSchedule: (id: string) => Promise<void>;
  /**
   * `GET /reports/schedules/:id/runs` (Admin) — what the last sends actually did.
   *
   * Worth surfacing because "it says it sent" and "somebody received it" are
   * different claims: a run refused by the outbound allow-list is recorded here with
   * the blocked addresses rather than looking like a quiet success.
   */
  reportScheduleRuns: (id: string) => Promise<ReportScheduleRun[]>;
  /**
   * `POST /reports/run/preview` (Admin) — what this configuration would contain.
   *
   * 🔥 REAL ROWS, CAPPED — the server runs the SAME query the emailed workbook is
   * built from and returns the first few rows of each tab with the true totals. The
   * preview is only worth having because it cannot disagree with the report.
   */
  previewReport: (request: ReportRunRequest) => Promise<ReportPreview>;
  /**
   * `POST /reports/run/workbook` (Admin) — run it once, now, and hand back the .xlsx.
   *
   * Base64 in the JSON body rather than a binary response: every other call in this
   * client is typed JSON over the MSAL-bearing fetch, and a second transport for one
   * endpoint is a second place for the auth header to be forgotten. A payroll-week
   * workbook is tens of kilobytes, so the encoding overhead buys consistency cheaply.
   *
   * WRITES NOTHING — it does not mark any schedule as having sent.
   */
  runReportWorkbook: (request: ReportRunRequest) => Promise<ReportWorkbook>;
  /**
   * `POST /time/liveness` — the every-15s heartbeat behind cross-device refresh and
   * the "someone else is on this entry" indicator (#1, #2).
   *
   * Returns NO session data: a watermark to compare, and who else is on the entries
   * asked about. Refetch the real list only when the watermark moves.
   */
  liveness: (body: {
    from: string;
    to: string;
    editing?: string | null;
    watching?: string[];
  }) => Promise<LivenessResult>;
  /** `POST /time/liveness/release` — drop this user's presence rows on page unload. */
  releaseLiveness: () => Promise<void>;
  /**
   * `POST /reports/bonus-import` (Admin) — import the production-bonus spreadsheet's
   * per-person rows. Creates bonus ENTRIES for review; pays nobody.
   */
  importBonusRows: (
    payPeriodEnding: string,
    rows: Array<{ name: string; amount: number }>
  ) => Promise<BonusImportResult>;
  /** `GET /reports/bonus-import/aliases` (Admin) — the stored name mappings. */
  listBonusAliases: () => Promise<BonusAlias[]>;
  /**
   * `POST /reports/bonus-import/aliases` (Admin) — decide what one name means.
   * `employeeId: null` records "not an employee", so it stops being re-reported.
   */
  mapBonusAlias: (name: string, employeeId: string | null) => Promise<BonusAlias>;
  /** `DELETE /reports/bonus-import/aliases/:id` (Admin). */
  deleteBonusAlias: (id: string) => Promise<void>;
  /** `GET /reports/commission/post-preview` — the same preview, for commission. */
  getCommissionPostPreview: (payPeriodEnding: string) => Promise<CommissionPostPlan>;
  /** `POST /reports/commission/post` — WRITES TO PAYROLL. Admin only. */
  postCommission: (payPeriodEnding: string) => Promise<CommissionPostResult>;
  /**
   * `GET /reports/amounts/:reportId` — the entries.
   *
   * `payPeriodEnding` scopes to ONE payroll week (Michael, 2026-09-17: bonuses are
   * per week, and two weeks listed together read as one larger amount). Omit for
   * every period.
   */
  listAmountEntries: (
    reportId: AmountReportId,
    payPeriodEnding?: string
  ) => Promise<AmountEntry[]>;
  getAmountOptions: (reportId: AmountReportId) => Promise<AmountEntryOptions>;
  createAmountEntry: (reportId: AmountReportId, input: AmountEntryInput) => Promise<AmountEntry>;
  /** Employee is NOT editable — delete and re-create to move an amount. */
  updateAmountEntry: (
    reportId: AmountReportId,
    id: string,
    input: Omit<AmountEntryInput, 'employeeId'>
  ) => Promise<AmountEntry>;
  deleteAmountEntry: (reportId: AmountReportId, id: string) => Promise<void>;

  // --- Production-line crews + the daily batch counts behind their bonus (Manager + Admin) ---
  /** `GET /reports/production-lines` — every line's crew and the last three weeks of counts. */
  getProductionLines: (weekEnding?: string) => Promise<ProductionLinesView>;
  addProductionLineMember: (line: string, employeeId: string) => Promise<void>;
  removeProductionLineMember: (line: string, employeeId: string) => Promise<void>;

  // --- Driver Logs: the weekly hours grid (Manager + Admin) ---
  // Every call returns the WHOLE week: one cell changes that driver's regular/overtime
  // split for the entire week, so patching a single cell locally would show wrong totals.
  getDriverLogWeek: (weekOf?: string) => Promise<DriverLogWeek>;
  upsertDriverLog: (input: DriverLogInput) => Promise<DriverLogWeek>;
  deleteDriverLog: (id: string) => Promise<DriverLogWeek>;
  /**
   * Approve (or withdraw approval for) driver-log entries — the review gate that
   * decides whether those hours may post. Returns the recomputed week, like every
   * other write here: approving changes nothing about the overtime split, but the
   * caller still needs the fresh statuses and one shape for all four calls is
   * cheaper to hold in your head than two.
   */
  setDriverLogApproval: (
    entryIds: readonly string[],
    approved: boolean,
    weekOf?: string
  ) => Promise<DriverLogWeek>;

  // --- NetSuite connection (/netsuite/connection, Admin) ---
  /** `GET /netsuite/connection` — the tenant's non-secret NS config + write gate. */
  getNsConnection: () => Promise<NsConnectionConfig>;
  /** `PATCH /netsuite/connection` — save non-secret config + the posting-enabled gate. */
  updateNsConnection: (input: UpdateNsConnectionInput) => Promise<NsConnectionConfig>;

  // --- Directory sync (/netsuite/*, Admin) ---
  /** `POST /netsuite/sync/departments` — name-match WorkLogix depts to NS. */
  syncDepartments: () => Promise<DepartmentSyncResult>;
  /** `POST /netsuite/sync/payroll-items` — upsert the synced earning-type list. */
  syncPayrollItems: () => Promise<PayrollItemSyncResult>;
  /**
   * `POST /netsuite/sync/employees` (Admin) — upsert the NS org chart (create +
   * annotate, org-chart fields ONLY). Returns the {@link EmployeeSyncSummary}
   * (upserted/created/annotated + system-supervisor + cycle reports).
   */
  syncEmployees: () => Promise<EmployeeSyncSummary>;
  /**
   * `GET /netsuite/employee-mappings` (Admin) — per-employee NS/org-chart
   * mapping rows with resolved supervisor + `needsAdminApproval` reason.
   */
  listEmployeeMappings: () => Promise<EmployeeSupervisorMapping[]>;
  /**
   * `GET /netsuite/user-links` (Admin) — Entra logins with their resolved
   * employee link. See {@link UserEmployeeLink} (matches Dev-B's projection).
   */
  listUserLinks: () => Promise<UserEmployeeLink[]>;
  /**
   * `PATCH /users/:id/employee-link` (Admin) — set/clear an Entra login's NS
   * employee link (`:id` is the `UserEmployeeLink.userId`). Dev-B returns
   * `{ ok: true }`; the caller ignores the body and reflects the submitted value
   * optimistically in local state.
   */
  setUserEmployeeLink: (id: string, input: SetUserEmployeeLinkRequest) => Promise<void>;
  /**
   * `POST /netsuite/sync/employee-earnings` (Admin) — re-mirror EVERY employee's
   * NetSuite Earnings sublist. Read-only against NetSuite; a full replace locally.
   */
  syncEmployeeEarnings: () => Promise<EmployeeEarningSyncResult>;
  /**
   * `GET /netsuite/employee-earnings` — every (employee → earning type) assignment.
   * This, not `Employee.nsPayrollItemId`, is what the roster's "Payroll item"
   * column shows: an employee holds several earnings, that field holds one.
   */
  listEmployeeEarnings: () => Promise<EmployeeEarningAssignment[]>;
  // --- Pay exceptions (/admin/pay-exceptions, Admin-only on every verb) ---
  /**
   * `GET /admin/pay-exceptions` — every per-employee pay exception, active first.
   * Deactivated rules are included: a suppressed override must be visible rather
   * than looking like it was never created.
   */
  listPayExceptions: () => Promise<PayException[]>;
  /** `POST /admin/pay-exceptions` — add one standing override. */
  createPayException: (input: PayExceptionWire) => Promise<PayException>;
  /**
   * `PUT /admin/pay-exceptions/:id` — replace one outright. PUT, not PATCH: which
   * value a rule needs depends on its field, so a partial body cannot be validated.
   * `employeeId` is ignored server-side — re-pointing an override at someone else
   * has to be a delete and a re-create.
   */
  updatePayException: (id: string, input: PayExceptionWire) => Promise<PayException>;
  // --- Co-supervision (/admin/supervisor-peers, Admin-only on every verb) ---
  /**
   * `GET /admin/supervisor-peers` — every co-supervision group with its members.
   *
   * 🔥 Michael, 2026-09-30: "Cody and Grayson have got to be able to see each others'
   * employees in WLX to approve for each other. They should be THE exception to the
   * rule where supervisors should only see the employees who work directly under them."
   *
   * Admin-only on the READS as well as the writes, matching pay exceptions: this
   * decides who may see and approve whose pay, and who can reach outside their own
   * team is not something a supervisor should be able to enumerate.
   */
  listSupervisorPeerGroups: () => Promise<SupervisorPeerGroup[]>;
  /** `GET /admin/supervisor-peers/candidates` — employees somebody actually reports to. */
  listSupervisorPeerCandidates: () => Promise<SupervisorPeerCandidate[]>;
  /** `POST /admin/supervisor-peers` — create a group. */
  createSupervisorPeerGroup: (input: SupervisorPeerGroupInput) => Promise<SupervisorPeerGroup>;
  /**
   * `PUT /admin/supervisor-peers/:id` — replace a group WHOLE, membership included.
   * PUT, not PATCH: "who covers for whom" is a property of the complete set, and
   * replacing it outright makes an add and a removal one atomic decision.
   */
  updateSupervisorPeerGroup: (
    id: string,
    input: SupervisorPeerGroupInput
  ) => Promise<SupervisorPeerGroup>;
  /** `DELETE /admin/supervisor-peers/:id` — end an arrangement outright. */
  deleteSupervisorPeerGroup: (id: string) => Promise<void>;

  /** `DELETE /admin/pay-exceptions/:id` — for a rule entered in error. */
  deletePayException: (id: string) => Promise<void>;

  /** `GET /netsuite/payroll-items` — synced NS payroll items (drives the picker). */
  listPayrollItems: () => Promise<NsPayrollItem[]>;
  /**
   * `PATCH /netsuite/departments/:id/mapping` — set/clear a department's
   * `ns_department_id`. Dev-B returns `{ ok: true }` (no row echoed), so the
   * caller updates its local copy optimistically from the submitted value.
   */
  updateDepartmentMapping: (id: string, input: UpdateDepartmentMappingInput) => Promise<void>;

  // --- Employees (/admin/employees) ---
  /** `GET /admin/employees` — the employee roster (never includes a PIN). */
  listEmployees: () => Promise<EmployeeRow[]>;
  /** `POST /admin/employees` — add an hourly employee (no PIN yet). */
  createEmployee: (input: CreateEmployeeInput) => Promise<Employee>;
  /** `PATCH /admin/employees/:id` — set an employee's NS id + payroll-item mapping. */
  updateEmployeeMapping: (id: string, input: UpdateEmployeeMappingInput) => Promise<EmployeeRow>;
  /** `PATCH /admin/employees/:id` `{ active }` — activate/deactivate an employee. */
  setEmployeeActive: (id: string, active: boolean) => Promise<EmployeeRow>;
  /** `POST /netsuite/employees/:id/sync-department` — mirror the NS home department. */
  syncEmployeeDepartment: (id: string) => Promise<EmployeeDepartmentSyncResult>;
  /**
   * `POST /admin/employees/:id/pin` — set or reset an employee PIN. The PIN is
   * write-only: the server hashes it (scrypt, per-row salt) and NEVER returns a
   * PIN. Resolves with no body (204).
   */
  setEmployeePin: (id: string, pin: string) => Promise<void>;
  /**
   * `DELETE /admin/employees/:id/pin` — remove an employee's PIN (admin-only).
   * Returns the updated employee (now with `hasPin: false`).
   */
  removeEmployeePin: (id: string) => Promise<EmployeeRow>;
  /** `GET /admin/employees/:id/allocations` — split mode + assigned departments. */
  getEmployeeAllocations: (id: string) => Promise<EmployeeAllocations>;
  /** `PUT /admin/employees/:id/allocations` — replace membership + split mode. */
  setEmployeeAllocations: (
    id: string,
    splitEnabled: boolean,
    allocations: DepartmentAllocation[]
  ) => Promise<EmployeeAllocations>;

  // --- Kiosks (/admin/kiosks) ---
  /** `GET /admin/kiosks` — registered kiosks (device token hash never exposed). */
  /**
   * `GET /admin/diagnostics` — deploy readiness. Reports whether each piece of
   * deploy-critical configuration is PRESENT (never its value), because neither
   * health endpoint can see a missing secret: the API resolves the PIN pepper on
   * every punch, so a build deployed without it breaks every clock-in while
   * `/health` still answers 200.
   */
  getDiagnostics: () => Promise<Diagnostics>;

  listKiosks: () => Promise<Kiosk[]>;
  /**
   * `POST /admin/kiosks` — register a kiosk to a department and MINT its device
   * token. The plaintext token is returned exactly ONCE on this response
   * ({@link KioskEnrollment.deviceToken}); it is never retrievable again.
   */
  registerKiosk: (input: RegisterKioskInput) => Promise<KioskEnrollment>;
  /**
   * `POST /admin/kiosks/:id/rotate-token` — invalidate the current device token
   * and mint a new one. The new plaintext token is returned exactly ONCE.
   */
  rotateKioskToken: (id: string) => Promise<KioskEnrollment>;
  /**
   * `PATCH /admin/kiosks/:id` `{ active: false }` — deactivate (revoke) a kiosk.
   * There is no dedicated revoke endpoint; revocation is an update. No token is
   * returned.
   */
  revokeKiosk: (id: string) => Promise<Kiosk>;
  /**
   * `DELETE /admin/kiosks/:id` — remove a kiosk for good. Intended for the test
   * kiosks left over from setting the estate up.
   *
   * 409s for a kiosk that has recorded time: those punches are the evidence
   * behind someone's pay, so a used kiosk is deactivated ({@link revokeKiosk}),
   * never deleted.
   */
  deleteKiosk: (id: string) => Promise<void>;
  /**
   * `PATCH /admin/kiosks/:id` `{ active: true }` — re-activate a previously
   * revoked kiosk. Like {@link revokeKiosk} this is just an update; it does NOT
   * mint a token, so a reactivated kiosk still needs a valid device token (rotate
   * to issue a fresh one if the old device was wiped).
   */
  reactivateKiosk: (id: string) => Promise<Kiosk>;
  /**
   * `PATCH /admin/kiosks/:id` `{ name }` — correct a kiosk's display name (a typo
   * at registration, or a renamed area). Cosmetic only: the device token, the
   * department binding and every punch already recorded are untouched.
   */
  renameKiosk: (id: string, name: string) => Promise<Kiosk>;
}

/** Build a `?a=b&c=d` query string from defined filter fields only. */
function toQuery(filters: SessionFilters | undefined): string {
  if (filters === undefined) {
    return '';
  }
  const params = new URLSearchParams();
  if (filters.from !== undefined && filters.from !== '') {
    params.set('from', filters.from);
  }
  if (filters.to !== undefined && filters.to !== '') {
    params.set('to', filters.to);
  }
  if (filters.departmentId !== undefined && filters.departmentId !== '') {
    params.set('departmentId', filters.departmentId);
  }
  if (filters.employeeId !== undefined && filters.employeeId !== '') {
    params.set('employeeId', filters.employeeId);
  }
  if (filters.supervisorId !== undefined && filters.supervisorId !== '') {
    params.set('supervisorId', filters.supervisorId);
  }
  if (filters.scope !== undefined) {
    params.set('scope', filters.scope);
  }
  /**
   * 🔥 `status` WAS NEVER SERIALISED. The long-shift banner has been sending
   * `status: 'long_shift'` since it was written, and this function dropped it — so the
   * banner fetched EVERY session in scope and re-filtered them in the browser. It
   * looked right because the client-side `stillClockedIn` filter saved it, while the
   * server-side rule built for exactly that query (see the `long_shift` case in
   * time.service.ts) was never once used.
   */
  if (filters.status !== undefined) {
    params.set('status', filters.status);
  }
  if (filters.postResult !== undefined) {
    params.set('postResult', filters.postResult);
  }
  const q = params.toString();
  return q.length > 0 ? `?${q}` : '';
}

/**
 * Build a {@link TimeAdminApi} over an existing {@link ApiClient}. Pure and
 * React-free so it can be constructed in tests with a stub client.
 */
export function createTimeAdminApi(client: ApiClient): TimeAdminApi {
  const enc = encodeURIComponent;
  return {
    listSessions: (filters) => client.request<TimeSession[]>(`/time/sessions${toQuery(filters)}`),
    patchSession: (id, input) =>
      client.request<TimeSession>(`/time/sessions/${enc(id)}`, { method: 'PATCH', body: input }),
    listSupervisors: () => client.request<TimeSupervisor[]>('/time/supervisors'),
    approveSessions: (sessionIds) =>
      client.request<BulkApproveOutcome[]>('/time/sessions/approve-bulk', {
        method: 'POST',
        body: { sessionIds },
      }),
    approveSession: (id) =>
      client.request<TimeSession>(`/time/sessions/${enc(id)}/approve`, { method: 'POST' }),
    acknowledgeSessionFlags: (id) =>
      client.request<TimeSession>(`/time/sessions/${enc(id)}/ack-flag`, { method: 'POST' }),
    getSessionOptions: () => client.request<TimeEntryOptions>('/time/session-options'),
    createSession: (input) =>
      client.request<TimeSession>('/time/sessions', { method: 'POST', body: input }),
    getMyLink: () => client.request<CallerLink>('/time/me/link'),
    previewPayrollRun: () =>
      client.request<PayrollRunPreview>('/payroll-run/preview', { method: 'POST', body: {} }),
    postToNetSuite: () =>
      client.request<TimeBillPostResult[]>('/time/post-to-netsuite', { method: 'POST' }),
    deleteTimeSession: (id) =>
      client.request<void>(`/admin/time-sessions/${enc(id)}`, { method: 'DELETE' }),
    listSessionPhotos: (id) =>
      client.request<SessionPhotosResponse>(`/time/sessions/${enc(id)}/photos`),

    listDepartments: () => client.request<Department[]>('/admin/departments'),
    listAvailableReports: () => client.request<AvailableReport[]>('/reports/available'),

    listHolidays: () => client.request<CompanyHoliday[]>('/reports/holiday'),
    getHolidayPay: (id) =>
      client.request<{ holiday: CompanyHoliday; pay: HolidayPay }>(
        `/reports/holiday/${enc(id)}/pay`
      ),
    getHolidayPeriodPay: (date) =>
      client.request<HolidayPeriodPay>(
        date === undefined
          ? '/reports/holiday/period-pay'
          : `/reports/holiday/period-pay?date=${enc(date)}`
      ),
    createHoliday: (input) =>
      client.request<CompanyHoliday>('/reports/holiday', { method: 'POST', body: input }),
    updateHoliday: (id, input) =>
      client.request<CompanyHoliday>(`/reports/holiday/${enc(id)}`, {
        method: 'PATCH',
        body: input,
      }),
    deleteHoliday: (id) =>
      client.request<void>(`/reports/holiday/${enc(id)}`, { method: 'DELETE' }),
    listPtoEntries: () => client.request<PtoEntry[]>('/reports/pto'),
    getPtoAccrual: (nsEmployeeId) =>
      client.request<PtoAccrualSnapshot>(`/reports/pto/accrual/${enc(nsEmployeeId)}`),
    getPtoStanding: (nsEmployeeId) =>
      client.request<PtoStanding>(`/reports/pto/standing/${enc(nsEmployeeId)}`),
    approvePtoEntry: (id) =>
      client.request<PtoPostResult>(`/reports/pto/${enc(id)}/approve`, { method: 'POST' }),
    getPtoOptions: () => client.request<PtoOptions>('/reports/pto/options'),
    createPtoEntry: (input) =>
      client.request<PtoEntry>('/reports/pto', { method: 'POST', body: input }),
    updatePtoEntry: (id, input) =>
      client.request<PtoEntry>(`/reports/pto/${enc(id)}`, { method: 'PATCH', body: input }),
    getDriverLogWeek: (weekOf) =>
      client.request<DriverLogWeek>(
        weekOf === undefined ? '/reports/driver-logs' : `/reports/driver-logs?weekOf=${enc(weekOf)}`
      ),
    upsertDriverLog: (input) =>
      client.request<DriverLogWeek>('/reports/driver-logs', { method: 'PUT', body: input }),
    deleteDriverLog: (id) =>
      client.request<DriverLogWeek>(`/reports/driver-logs/${enc(id)}`, { method: 'DELETE' }),
    setDriverLogApproval: (entryIds, approved, weekOf) =>
      client.request<DriverLogWeek>(`/reports/driver-logs/${approved ? 'approve' : 'unapprove'}`, {
        method: 'POST',
        body: { entryIds: [...entryIds], ...(weekOf === undefined ? {} : { weekOf }) },
      }),
    getRepSales: (employeeIds, from, to) =>
      client.request<RepSalesResponse>(
        `/netsuite/rep-sales?employeeIds=${enc(employeeIds.join(','))}` +
          `&from=${enc(from)}&to=${enc(to)}`
      ),
    getCommissionReport: (payPeriodEnding) =>
      client.request<CommissionReport>(
        `/reports/commission?payPeriodEnding=${enc(payPeriodEnding)}`
      ),
    liveness: (body) =>
      client.request<LivenessResult>('/time/liveness', { method: 'POST', body }),
    releaseLiveness: () =>
      client.request<void>('/time/liveness/release', { method: 'POST' }),
    importBonusRows: (payPeriodEnding, rows) =>
      client.request<BonusImportResult>('/reports/bonus-import', {
        method: 'POST',
        body: { payPeriodEnding, rows },
      }),
    listBonusAliases: () => client.request<BonusAlias[]>('/reports/bonus-import/aliases'),
    mapBonusAlias: (name, employeeId) =>
      client.request<BonusAlias>('/reports/bonus-import/aliases', {
        method: 'POST',
        body: { name, employeeId },
      }),
    deleteBonusAlias: (id) =>
      client.request<void>(`/reports/bonus-import/aliases/${enc(id)}`, { method: 'DELETE' }),
    listReportSchedules: () => client.request<StoredReportSchedule[]>('/reports/schedules'),
    createReportSchedule: (input) =>
      client.request<StoredReportSchedule>('/reports/schedules', { method: 'POST', body: input }),
    updateReportSchedule: (id, input) =>
      client.request<StoredReportSchedule>(`/reports/schedules/${enc(id)}`, {
        method: 'PUT',
        body: input,
      }),
    deleteReportSchedule: (id) =>
      client.request<void>(`/reports/schedules/${enc(id)}`, { method: 'DELETE' }),
    reportScheduleRuns: (id) =>
      client.request<ReportScheduleRun[]>(`/reports/schedules/${enc(id)}/runs`),
    previewReport: (request) =>
      client.request<ReportPreview>('/reports/run/preview', { method: 'POST', body: request }),
    runReportWorkbook: (request) =>
      client.request<ReportWorkbook>('/reports/run/workbook', { method: 'POST', body: request }),
    listRecurringAmounts: (reportId) =>
      client.request<AmountRecurring[]>(`/reports/amounts/${enc(reportId)}/recurring`),
    createRecurringAmount: (reportId, input) =>
      client.request<AmountRecurring>(`/reports/amounts/${enc(reportId)}/recurring`, {
        method: 'POST',
        body: input,
      }),
    stopRecurringAmount: (reportId, id) =>
      client.request<void>(`/reports/amounts/${enc(reportId)}/recurring/${enc(id)}`, {
        method: 'DELETE',
      }),
    getAmountPostPreview: (reportId, payPeriodEnding) =>
      client.request<AmountPostPlan>(
        `/reports/amounts/${enc(reportId)}/post-preview?payPeriodEnding=${enc(payPeriodEnding)}`
      ),
    postAmounts: (reportId, payPeriodEnding) =>
      client.request<AmountPostResult>(
        `/reports/amounts/${enc(reportId)}/post?payPeriodEnding=${enc(payPeriodEnding)}`,
        { method: 'POST' }
      ),
    postAllAmounts: (payPeriodEnding) =>
      client.request<ConsolidatedPostResult>(
        `/reports/amounts/post-all?payPeriodEnding=${enc(payPeriodEnding)}`,
        { method: 'POST' }
      ),
    sweepAllowances: (payPeriodEnding) =>
      client.request<AllowanceSweepResult>(
        `/reports/amounts/reimbursement/sweep-allowances?payPeriodEnding=${enc(payPeriodEnding)}`,
        { method: 'POST' }
      ),
    getCommissionPostPreview: (payPeriodEnding) =>
      client.request<CommissionPostPlan>(
        `/reports/commission/post-preview?payPeriodEnding=${enc(payPeriodEnding)}`
      ),
    postCommission: (payPeriodEnding) =>
      client.request<CommissionPostResult>(
        `/reports/commission/post?payPeriodEnding=${enc(payPeriodEnding)}`,
        { method: 'POST' }
      ),
    listAmountEntries: (reportId, payPeriodEnding) =>
      client.request<AmountEntry[]>(
        payPeriodEnding === undefined
          ? `/reports/amounts/${enc(reportId)}`
          : `/reports/amounts/${enc(reportId)}?payPeriodEnding=${enc(payPeriodEnding)}`
      ),
    getAmountOptions: (reportId) =>
      client.request<AmountEntryOptions>(`/reports/amounts/${enc(reportId)}/options`),
    createAmountEntry: (reportId, input) =>
      client.request<AmountEntry>(`/reports/amounts/${enc(reportId)}`, {
        method: 'POST',
        body: input,
      }),
    updateAmountEntry: (reportId, id, input) =>
      client.request<AmountEntry>(`/reports/amounts/${enc(reportId)}/${enc(id)}`, {
        method: 'PATCH',
        body: input,
      }),
    deleteAmountEntry: (reportId, id) =>
      client.request<void>(`/reports/amounts/${enc(reportId)}/${enc(id)}`, { method: 'DELETE' }),
    getProductionLines: (weekEnding) =>
      client.request<ProductionLinesView>(
        weekEnding === undefined
          ? '/reports/production-lines'
          : `/reports/production-lines?weekEnding=${enc(weekEnding)}`
      ),
    addProductionLineMember: (line, employeeId) =>
      client.request<void>(`/reports/production-lines/${enc(line)}/members`, {
        method: 'POST',
        body: { employeeId },
      }),
    removeProductionLineMember: (line, employeeId) =>
      client.request<void>(
        `/reports/production-lines/${enc(line)}/members/${enc(employeeId)}`,
        { method: 'DELETE' }
      ),
    deletePtoEntry: (id) => client.request<void>(`/reports/pto/${enc(id)}`, { method: 'DELETE' }),

    getNsConnection: () => client.request<NsConnectionConfig>('/netsuite/connection'),
    updateNsConnection: (input) =>
      client.request<NsConnectionConfig>('/netsuite/connection', { method: 'PATCH', body: input }),

    syncDepartments: () =>
      client.request<DepartmentSyncResult>('/netsuite/sync/departments', { method: 'POST' }),
    syncPayrollItems: () =>
      client.request<PayrollItemSyncResult>('/netsuite/sync/payroll-items', { method: 'POST' }),
    syncEmployees: () =>
      client.request<EmployeeSyncSummary>('/netsuite/sync/employees', { method: 'POST' }),
    listEmployeeMappings: () =>
      client.request<EmployeeSupervisorMapping[]>('/netsuite/employee-mappings'),
    listUserLinks: () => client.request<UserEmployeeLink[]>('/netsuite/user-links'),
    setUserEmployeeLink: (id, input) =>
      client.request<void>(`/users/${enc(id)}/employee-link`, { method: 'PATCH', body: input }),
    listPayrollItems: () => client.request<NsPayrollItem[]>('/netsuite/payroll-items'),
    syncEmployeeEarnings: () =>
      client.request<EmployeeEarningSyncResult>('/netsuite/sync/employee-earnings', {
        method: 'POST',
      }),
    listEmployeeEarnings: () =>
      client.request<EmployeeEarningAssignment[]>('/netsuite/employee-earnings'),

    listPayExceptions: () => client.request<PayException[]>('/admin/pay-exceptions'),
    createPayException: (input) =>
      client.request<PayException>('/admin/pay-exceptions', { method: 'POST', body: input }),
    updatePayException: (id, input) =>
      client.request<PayException>(`/admin/pay-exceptions/${enc(id)}`, {
        method: 'PUT',
        body: input,
      }),
    deletePayException: (id) =>
      client.request<void>(`/admin/pay-exceptions/${enc(id)}`, { method: 'DELETE' }),

    listSupervisorPeerGroups: () =>
      client.request<SupervisorPeerGroup[]>('/admin/supervisor-peers'),
    listSupervisorPeerCandidates: () =>
      client.request<SupervisorPeerCandidate[]>('/admin/supervisor-peers/candidates'),
    createSupervisorPeerGroup: (input) =>
      client.request<SupervisorPeerGroup>('/admin/supervisor-peers', {
        method: 'POST',
        body: input,
      }),
    updateSupervisorPeerGroup: (id, input) =>
      client.request<SupervisorPeerGroup>(`/admin/supervisor-peers/${enc(id)}`, {
        method: 'PUT',
        body: input,
      }),
    deleteSupervisorPeerGroup: (id) =>
      client.request<void>(`/admin/supervisor-peers/${enc(id)}`, { method: 'DELETE' }),
    updateDepartmentMapping: (id, input) =>
      client.request<void>(`/netsuite/departments/${enc(id)}/mapping`, {
        method: 'PATCH',
        body: input,
      }),

    listEmployees: () => client.request<EmployeeRow[]>('/admin/employees'),
    createEmployee: (input) =>
      client.request<Employee>('/admin/employees', { method: 'POST', body: input }),
    updateEmployeeMapping: (id, input) =>
      client.request<EmployeeRow>(`/admin/employees/${enc(id)}`, { method: 'PATCH', body: input }),
    setEmployeeActive: (id, active) =>
      client.request<EmployeeRow>(`/admin/employees/${enc(id)}`, {
        method: 'PATCH',
        body: { active },
      }),
    syncEmployeeDepartment: (id) =>
      client.request<EmployeeDepartmentSyncResult>(
        `/netsuite/employees/${enc(id)}/sync-department`,
        { method: 'POST' }
      ),
    setEmployeePin: (id, pin) =>
      client.request<void>(`/admin/employees/${enc(id)}/pin`, { method: 'POST', body: { pin } }),
    removeEmployeePin: (id) =>
      client.request<EmployeeRow>(`/admin/employees/${enc(id)}/pin`, { method: 'DELETE' }),
    getEmployeeAllocations: (id) =>
      client.request<EmployeeAllocations>(`/admin/employees/${enc(id)}/allocations`),
    setEmployeeAllocations: (id, splitEnabled, allocations) =>
      client.request<EmployeeAllocations>(`/admin/employees/${enc(id)}/allocations`, {
        method: 'PUT',
        body: { splitEnabled, allocations },
      }),

    getDiagnostics: () => client.request<Diagnostics>('/admin/diagnostics'),
    listKiosks: () => client.request<Kiosk[]>('/admin/kiosks'),
    registerKiosk: (input) =>
      client.request<KioskEnrollment>('/admin/kiosks', { method: 'POST', body: input }),
    rotateKioskToken: (id) =>
      client.request<KioskEnrollment>(`/admin/kiosks/${enc(id)}/rotate-token`, { method: 'POST' }),
    revokeKiosk: (id) =>
      client.request<Kiosk>(`/admin/kiosks/${enc(id)}`, {
        method: 'PATCH',
        body: { active: false },
      }),
    deleteKiosk: (id) => client.request<void>(`/admin/kiosks/${enc(id)}`, { method: 'DELETE' }),
    reactivateKiosk: (id) =>
      client.request<Kiosk>(`/admin/kiosks/${enc(id)}`, {
        method: 'PATCH',
        body: { active: true },
      }),
    renameKiosk: (id, name) =>
      client.request<Kiosk>(`/admin/kiosks/${enc(id)}`, {
        method: 'PATCH',
        body: { name },
      }),
  };
}

/**
 * React hook exposing the time/admin API bound to the signed-in user's token
 * and the `VITE_API_BASE` origin. Memoized on the token acquirer so consumers
 * get a stable reference across renders.
 */
export function useTimeAdminApi(): TimeAdminApi {
  const { acquireToken } = useAuth();

  return useMemo(() => {
    const client = createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE,
      getToken: acquireToken,
    });
    return createTimeAdminApi(client);
  }, [acquireToken]);
}
